import { TextButton } from '@creditornot/cb-button';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { PerformanceByVenueInsight } from 'apis/merchant-gateway/insights';
import { InfiniteTable } from 'modules/experience-core';

import usePerformanceByVenueTableProps from './hooks/usePerformanceByVenueTableProps';
import { ContentRow, InsightResponsePaginated, RowType } from '../../types';
import SummaryTable from '../InsightSummary/SummaryTable';
import PerformanceByVenueTable, { VenueTableSortableColumnIDs } from '../tables/performance/PerformanceByVenueTable';

type PerformanceByVenueCardProps = {
  isVATEnabled: boolean;
  performanceByVenueInsights: InsightResponsePaginated<PerformanceByVenueInsight>;
  sort: InfiniteTable.SortState<VenueTableSortableColumnIDs> | undefined;
  setSort: (sort: InfiniteTable.SortState<VenueTableSortableColumnIDs> | undefined) => void;
  onShowAll: () => void;
};

const ShowAllButton = styled(TextButton)`
  flex-grow: 1;
  width: 100%;
  justify-content: center;
`;

export const PerformanceByVenueCard = ({
  isVATEnabled,
  performanceByVenueInsights,
  sort,
  setSort,
  onShowAll,
}: PerformanceByVenueCardProps) => {
  const { formatMessage } = useIntl();

  const tableProps = usePerformanceByVenueTableProps({ isVATEnabled, sort, setSort, performanceByVenueInsights });

  const additionalRows: ContentRow[] = performanceByVenueInsights.hasNextPage
    ? [
        {
          type: RowType.Raw,
          key: 'show-all',
          content: (
            <ShowAllButton onClick={onShowAll}>
              <FormattedMessage id="insights.performance.table.show-all" />
            </ShowAllButton>
          ),
        },
      ]
    : [];

  return (
    <SummaryTable
      onRetry={performanceByVenueInsights.refetch}
      isError={
        performanceByVenueInsights.isError ||
        (performanceByVenueInsights.isLoading && !performanceByVenueInsights.isFetching)
      }
      isFetching={performanceByVenueInsights.isFetching}
      title={formatMessage({ id: 'insights.performance.table.performance-by-venue' })}
      rows={[
        {
          type: RowType.Raw,
          key: 'raw',
          content: <PerformanceByVenueTable {...tableProps} />,
        },
        ...additionalRows,
      ]}
    />
  );
};
